import React, {Fragment} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Classes from '../common/Classes';
import {
  CallToActionConstants,
  Claims,
  ConsentGroups,
  DataDtmActions,
  EXHIBITOR_DIRECTORY_RECOMMENDATION,
  ImageTypes,
  RegRedirectConstants
} from '../common/Constants';
import DetailsService from './DetailsService';
import ProductMapper from '../common/exhibitingOrganisation/ProductMapper';
import TranslationService from '../translations';
import ShowPlanService from '../common/api/ShowPlanService';
import Tags from '../common/dtmTags';
import ErrorContainer from '../common/components/ErrorContainer';
import StringFormatter from '../common/StringFormatter';
import Utils from '../common/Utils';
import BigCircleSpinner from '../common/components/BigCircleSpinner';
import ProductDetailsImage from './ProductDetailsImage';
import ProductDetailsVideo from './ProductDetailsVideo';
import FavouriteComponent from '../directory/packages/components/FavouriteComponent';
import Tag from '../common/components/Tag';
import ProductCarouselWrapper from '../profile/exhibitorPage/products/ProductCarouselWrapper';
import EventEditionService from '../common/api/EventEditionService';
import {VideoWrapper} from '../common/components/VideoWrapper';
import VideoService from '../profile/api/VideoService';
import authorizationStore from '../common/api/authorizationStore';
import EventEdititonFeatureConfigService from '../profile/api/EventEdititonFeatureConfigService';
import ProductRestriction from '../common/buyers/productRestriction'
import CallToActionComponent from '../common/components/CallToActionComponent';
import LeadService from '../exhibitorHub/api/LeadService';
import DocumentsSection from './DocumentsSection';
import '../../styles/discover.less';
import Banner from '@reedexpo/marketplace-banner';
import {trackMixpanelEvent} from '../common/MixpanelHelper';
import mixpanelConstants from '../common/mixpanelConstants';

class ProductDetails extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      imageUrl: '',
      isInnovative: false,
      title: '',
      description: '',
      companyName: '',
      otherProducts: [],
      isLoading: true,
      attributes: [],
      filterAttributes: [],
      errors: [],
      fieldErrors: {},
      organisationId: '',
      producerDetails: [],
      packageId: '',
      isProductEnabled: false,
      isVideoEnabled : false,
      videoStatus: false,
      videoId: '',
      partnerVideoUuid: '',
      isAuthorised: false,
      isProductDirectoryNavigationEnabled: false,
      callToActionFailed: false,
      callToActionDisabled: false,
      isAtlas20Show: false,
      isProductIngredientType: false,
      eventEditionId: '',
      featureFilterName: null,
      documents: []
    };
  }

  componentDidMount() {
    authorizationStore.getToken()
      .then((token) => {
        if (token) {
          this.setState({
            isAuthorised: true
          });
        }
      });

    Promise.all([
      EventEditionService.getEventEdition(this.props.baseApiUrl, this.props.context.interfaceLocale, this.props.context.eventEditionId),
      DetailsService.fetchExhibitorDetails(this.props.baseApiUrl, this.props.context.exhibitingOrganisationId, this.props.context.interfaceLocale,false),
      EventEdititonFeatureConfigService.isProductDirectoryNavigationFeatureEnabled(this.props.baseApiUrl, this.props.context.eventEditionId),
      authorizationStore.getToken()
    ])
      .then(async ([eventEditionResponse, exhibitorDetailsResponse, isProductDirectoryNavigationFeatureEnabled, authorizationToken]) => {
        if (Utils.serviceResultHasError(exhibitorDetailsResponse)) {
          this.updateStateFromService(exhibitorDetailsResponse);
          return;
        }
        if (eventEditionResponse !== null) {
          this.setState({
            isAtlas20Show: eventEditionResponse.isAtlas20Show
          });
        }
        const mappedProducts = ProductMapper.fromResponseWithAttributes(
            exhibitorDetailsResponse,
            this.props.context.interfaceLocale, this.props.context.primaryLocale
        ).products;
        const mappedProduct = mappedProducts.find(prd => prd.id === this.props.context.productId);
        if (!mappedProduct) {
          this.setState({
            errors: ['invalid_product_id'],
            isLoading: false
          });
          return;
        }

        const restrictProductDetails = eventEditionResponse.isParticipationEvent && window.featureFilters?.restrictProductDetails;

        const checkIsValidBuyer = async (restrictProductDetails) => {
          ProductRestriction.checkIsValidBuyer(this.props.baseApiUrl,
              this.props.context.eventEditionId,
              this.props.context.interfaceLocale)
              .then((isValidBuyer) => {
                this.setState({
                  isProductEnabled: restrictProductDetails ? isValidBuyer : true,
                  isVideoEnabled: isValidBuyer
                })
              });
          const featureFilter = await ProductRestriction.getUnrestrictedFeatureFilter(this.props.baseApiUrl, this.props.context.eventEditionId, this.props.context.interfaceLocale)
          if (featureFilter !== null) {
            this.setState({
              featureFilterName: featureFilter.multilingualNames[0].name
            });
          }
        };

        if (restrictProductDetails) {
          await checkIsValidBuyer(true);
        } else {
          let isVideoEnabled = eventEditionResponse.isVideoEnabled;

          if (!window.featureFilters || (window.featureFilters && this.props.context.eventEditionId !== window.featureFilters.eventEditionId) || !isVideoEnabled) {
            this.setState({
              isProductEnabled: true,
              isVideoEnabled: isVideoEnabled
            });
          } else {
            await checkIsValidBuyer(false);
          }
        }

        const displayNameInInterfaceLocale = exhibitorDetailsResponse.multilingual
            .find(x => x.locale.toLowerCase() === this.props.context.interfaceLocale.toLowerCase());
        const displayNameInPrimaryLocale = exhibitorDetailsResponse.multilingual
            .find(x => x.locale.toLowerCase() === this.props.context.primaryLocale.toLowerCase());
        const {displayName} = (displayNameInInterfaceLocale || displayNameInPrimaryLocale);

        mappedProduct['packageId'] = exhibitorDetailsResponse.packageId;
        mappedProduct['exhibitingOrganisationName'] = displayName;

        if (this.props.context.showPlanningEnabled) {
          this.getCurrentShowPlan(
              this.props.context.eventEditionId,
              this.props.baseApiUrl,
              Utils.getShowPlanId(this.props.context.eventEditionId)
          );
        }

        this.setState({
          product: mappedProduct,
          documents: mappedProduct.documents,
          companyName: displayName,
          title: mappedProduct.name,
          attributes: mappedProduct.attributes,
          filterAttributes: mappedProduct.filterAttributes,
          description: mappedProduct.description,
          imageUrl: mappedProduct.imageUrl,
          isInnovative: mappedProduct.isInnovative,
          otherProducts: mappedProducts.filter(prd => prd.id !== this.props.context.productId),
          isLoading: false,
          organisationId: exhibitorDetailsResponse.organisationId,
          producerDetails: mappedProduct.producerDetails,
          packageId: exhibitorDetailsResponse.packageId,
          videoId: mappedProduct.videoId,
          ...(mappedProduct.videoId && { partnerVideoUuid: mappedProduct.video.partnerVideoUuid }),
          ...(mappedProduct.videoId && { videoStatus: mappedProduct.video.status === 'Successful' ? true : false }),
          isAuthorised: !!authorizationToken,
          isProductDirectoryNavigationEnabled: isProductDirectoryNavigationFeatureEnabled,
          eventEditionId: exhibitorDetailsResponse.eventEditionId
        });

        if (Utils.isDiscoverEnabled(this.state.eventEditionId)) {
          const currentProduct = exhibitorDetailsResponse.products.filter(p => p.id === this.props.context.productId);
          this.setState(prevState => ({
            isProductIngredientType: Utils.haveValidCategory(currentProduct, prevState.eventEditionId)
          }));
        }

        trackMixpanelEvent(mixpanelConstants.eventName.productDetailsVisited, {
          productId: this.props.context.productId,
          productName: mappedProduct.name,
          exhibitorId: this.props.context.exhibitingOrganisationId,
          exhibitorName: displayName,
          packageName: Utils.getPackageName(exhibitorDetailsResponse.packageId),
          component: mixpanelConstants.component.productDetails
        })
        this.lastVisitedExhibitorDetails(this.props.context.exhibitingOrganisationId, displayName);
      });
  }

  getCurrentShowPlan = (eventEditionId, baseApiUrl, showPlanId) => {
    ShowPlanService.getShowPlan(
      baseApiUrl,
      eventEditionId,
      showPlanId
    )
      .then((serviceResult) => {
        this.setState({showPlan: serviceResult.showPlan});
      });
  };


  getPkceRedirectionUrl = (redirectUrl) => {
    const host = Utils.getLocationOrigin();
    const state = {
      [RegRedirectConstants.REDIRECT_URL]: Buffer.from(redirectUrl)
        .toString('base64'),
      [RegRedirectConstants.PRODUCT_TYPE]: RegRedirectConstants.ATLAS20SHOW
    };
    const environment = Utils.getCurrentEnvironment();
    const url = Utils.isDevEnvironment(environment) ? RegRedirectConstants.PKCE_REDIRECT_URL_DEV
      : `${host}${RegRedirectConstants.PKCE_SIGNUP_REDIRECT_URL}`;
    return `${url}&state=${JSON.stringify(state)}`;
  };
  getRegRedirectUrl = () => {
    const host = Utils.getLocationOrigin();
    const environment = Utils.getCurrentEnvironment();
    return Utils.isDevEnvironment(environment) ? RegRedirectConstants.REG_REDIRECT_URL_DEV
      : `${host}${RegRedirectConstants.REG_REDIRECT_URL}`;
  };

  updateStateFromService = (serviceResult) => {
    this.setState({
      errors: serviceResult.errors,
      fieldErrors: serviceResult.fieldErrors,
      isLoading: false
    });
  };

  sortProductAttributes = pa => (pa.sort((pa1, pa2) => pa1.sortOrder - pa2.sortOrder));

  join = answers => (answers.reduce((acc, ans) => `${acc}, ${ans}`));

  displayProducerLink = (producerDetails, producerUrlFormat) => {
    if (producerDetails === undefined || producerDetails === null) {
      return null;
    }
    return producerDetails.map((x) => {
      const producerUrl = StringFormatter.format(producerUrlFormat, x.id);
      return <a className="producer-title-link" key={x.id} href={producerUrl}>{`${x.firstName} ${x.lastName}`}</a>;
    });
  };

  isProductInShowPlan = (productId, showPlan) => showPlan?.productIds?.includes(productId);

  checkIfContactRequestSentForProduct = (productId) => {
    const productList = JSON.parse(Utils.getFromLocalStorage(CallToActionConstants.localStorageProductKey));
    if (productList == null) {
      return false;
    }
    return (productList.includes(productId));
  };

  callToActionButtonClick = async (productId, productTitle) => {
    if (this.state.isAtlas20Show) {
      if (!this.state.isAuthorised) {
        const knownUrl = this.getPkceRedirectionUrl(this.props.navigation.participantProtectedDirectory);
        const unknownUrl = this.getRegRedirectUrl();
        const url = Utils.constructLoginEmailUrl(this.props.idpUrl, knownUrl, unknownUrl, this.props.context.eventEditionId, this.props.context.primaryLocale);
        Utils.setLocationHref(url);
        return;
      }
      const hasEventEditionClaim = await authorizationStore.hasClaim(false, Claims.EventEditionClaim, this.props.context.eventEditionId);
      if (!hasEventEditionClaim) {
        const state = {
          [RegRedirectConstants.PRODUCT_TYPE]: RegRedirectConstants.ATLAS20SHOW
        };
        const redirectUrl = this.getRegRedirectUrl();
        const url = `${redirectUrl}?state=${JSON.stringify(state)}`;
        Utils.setLocationHref(url);
        return;
      }
    }
    let productList = JSON.parse(Utils.getFromLocalStorage(CallToActionConstants.localStorageProductKey));
    if (productList == null) {
      productList = [];
    }
    const createdOn = new Date(Date.now()).toISOString();
    await LeadService.createLeadForProduct(
      this.props.baseApiUrl, this.props.context.interfaceLocale,
      this.props.context.exhibitingOrganisationId, this.props.context.eventEditionId, createdOn, productId, productTitle
    )
      .then((response) => {
        if (response === null) {
          this.setState({callToActionFailed: true});
        } else {
          if (!productList.includes(productId)) {
            productList.push(productId);
            Utils.setValueInLocalStorage(CallToActionConstants.localStorageProductKey, JSON.stringify(productList));
          }
          if (!this.state.callToActionDisabled) {
            this.setState({callToActionDisabled: true});
          }
        }
      });
  };

  getExhibitorLink = (organisationId, companyName) => {
    let detailPageLink;
    const environment = Utils.getCurrentEnvironment();
    if(!Utils.isDevEnvironment(environment)  && !Utils.isShowEnvironment(environment) && companyName) {
      //replaces spaces with '-' from company name with meaning full name
      let exhibitorName = Utils.getSEOFriendlyName(companyName);
      detailPageLink = StringFormatter.format(this.props.navigation.exhibitorPublicDetailsUrlFormat,
          exhibitorName.concat('.', organisationId));
    }
    else{
      detailPageLink = StringFormatter.format(this.props.navigation.exhibitorPublicDetailsUrlFormat,
          organisationId);
    }
    return detailPageLink;
  }

  lastVisitedExhibitorDetails = (exhibitorId,exhibitorName) =>{
    if (!Utils.hasConsent(ConsentGroups.functional)) {
      return;
    }
    const lastVisitedExhibitorInfo = {
      'exhibitorId': exhibitorId,
      'exhibitorName': exhibitorName
    }
     Utils.setValueInLocalStorage(EXHIBITOR_DIRECTORY_RECOMMENDATION.lastVisitedExhibitor, JSON.stringify(lastVisitedExhibitorInfo));
   }

   handleTrackMixpanelEvent = () => {
    trackMixpanelEvent(mixpanelConstants.eventName.productInteracted, {
      exhibitorId: this.props.context.exhibitingOrganisationId,
      productId: this.props.context.productId,
      subComponent: mixpanelConstants.subComponent.standard,
      productName: this.state.title,
      exhibitorName: this.state.companyName,
      packageName: Utils.getPackageName(this.state.packageId),
      component: mixpanelConstants.component.productDetails,
      element: mixpanelConstants.element.navigationArrows
    });
  }

  render() {
    const translations = TranslationService.getTranslations(this.props.context.interfaceLocale);
    const {
      title,
      description,
      attributes,
      filterAttributes,
      otherProducts,
      producerDetails,
    } = this.state;
    const tags = Tags.productDetails;
    if (this.state.isLoading) {
      return <BigCircleSpinner/>;
    }
    if (this.state.errors.length > 0 || Object.keys(this.state.fieldErrors).length > 0) {
      return <ErrorContainer errors={[translations.errorMessages.client_error]}/>;
    }
    const productAttributes = [...filterAttributes || [], ...attributes || []];

    if (!this.state.isProductEnabled) {
      return <ErrorContainer errors={[this.state.featureFilterName +' ' + translations.productDetails.usersOnlyDetails]}/>;
    }
    const discoverPageUrl = Utils.getDiscoverConfig().discoverPageUrl;
    return (
        <div
          className={classNames(Classes.productContainerViewMode)}
          data-dtm={tags.section}
          data-dtm-actions={DataDtmActions.view}
          data-dtm-attributes={JSON.stringify({
            productId: this.props.context.productId,
            exhibitorId: this.props.context.exhibitingOrganisationId,
            package: Utils.getPackageName(this.state.packageId)
          })}
        >
          {this.state.isVideoEnabled &&
          this.state.videoStatus && this.state.isAuthorised ?
            (
              <div
                className={Classes.content}
                data-dtm={tags.video}
                data-dtm-attributes={JSON.stringify({exhibitorId: this.props.context.exhibitingOrganisationId,
                  productId: this.props.context.productId})}
              >
                <VideoWrapper
                  sourceUrl="https://bitmovin-a.akamaihd.net/content/sintel/hls/playlist.m3u8"
                  posterUrl={this.state.imageUrl}
                  autoplay={false}
                  videoId={this.state.videoId}
                  reactSettingsName="reactSettingsProductDetails"
                />
              </div>
          )
          : (this.state.videoId != null && this.state.partnerVideoUuid != null) ?
            (<ProductDetailsVideo
              videoId={this.state.partnerVideoUuid}
              productId={this.props.context.productId}
              productName={this.state.title}
              exhibitorId={this.props.context.exhibitingOrganisationId}
              exhibitorName={this.state.companyName}
              packageName={Utils.getPackageName(this.state.packageId)}
             />) :
             (<ProductDetailsImage
              src={this.state.imageUrl}
              imageSize={ImageTypes.productDetails}
              alt={translations.profile.products.productImageAltText}
              translations={translations.profile.products}
              isRounded
              isEditable={false}
            />)
          }
          <hr/>
          <div>
            {this.state.isInnovative &&
              <Tag
                tagName={translations.productDetails.innovativeClipText}
                dataDtm={tags.innovative}
                dataDtmAttribute={{productId: this.props.context.productId}}
                classes={Classes.productInnovativeTag}
              />
            }
            <div
              className="producer-details-title">{this.displayProducerLink(producerDetails, this.props.navigation.artistDetailsUrlFormat)}</div>
            <div className={Classes.productTitle}>{title}</div>
          </div>
          <FavouriteComponent
            product={this.state.product}
            translations={translations.productDirectory.favourite}
            initialState={this.isProductInShowPlan(this.props.context.productId, this.state.showPlan)}
            showPlanningEnabled={this.props.context.showPlanningEnabled}
            className={Classes.showPlanContainer}
            type="product"
            dtmData={Tags.productDetails}
            component={mixpanelConstants.component.productDetails}
          />
          {description ? <div className={Classes.productDescription}>{description}</div> : null}

          {this.state.documents !== null ?
            <DocumentsSection
                translations={translations.productDetails.documents}
                documents={this.state.documents}
                tags={tags.documents}
                locale={this.props.context.interfaceLocale}
                exhibitorId= {this.props.context.exhibitingOrganisationId}
                productId = {this.props.context.productId}
                mixpanelProps={JSON.stringify({
                  exhibitorName: this.state.companyName,
                  productName: this.state.title,
                  component: mixpanelConstants.component.productDetails,
                  packageName: Utils.getPackageName(this.state.packageId)
                })}
              />
            : null }

          <div className={Classes.productAttributes}>
            <div className={Classes.productText}>
              {this.sortProductAttributes(productAttributes)
                .map(pa =>
                  (
                    <p key={pa.question} className={Classes.productAttributeSection}><span>{pa.question} : </span>
                      <span className={Classes.productAttributeAnswers}>{this.join(pa.answers)}</span>
                    </p>))}
            </div>
          </div>
          {(Utils.isParticipantBase(this.props.context.mode, this.props.context.tenants) || this.state.isAtlas20Show) ?
            <div>
              <hr className={Classes.line}/>
              <CallToActionComponent
                handleClick={() => this.callToActionButtonClick(this.props.context.productId, title)}
                dataDtm={Tags.productDetails.productDetails_callToAction}
                dataDtmAction={DataDtmActions.click}
                dataDtmAttribute={{productId: this.props.context.productId}}
                disabled={this.checkIfContactRequestSentForProduct(this.props.context.productId)}
                callToActionFailed={this.state.callToActionFailed}
                callToActionBtnStatusContent={translations.productDetails}
              />
            </div> : null}
          <div className={Classes.productText}>
            <hr className={Classes.line}/>
            <p>{translations.productDetails.exhibitorNameHeader}</p>
            <div
                onClick={() => trackMixpanelEvent(mixpanelConstants.eventName.exhibitorClicked, {exhibitorId: this.props.context.exhibitingOrganisationId, exhibitorName: this.state.companyName,
                    packageName: Utils.getPackageName(this.state.packageId), element: mixpanelConstants.element.exhibitorTitle, component: mixpanelConstants.component.productDetails,
                    productId: this.props.context.productId, productName: this.state.title})}
            >
              <a
                href={this.getExhibitorLink(this.state.organisationId, this.state.companyName)}
                className={Classes.productExhibitorName}
                data-dtm={tags.exhibitorLink}
                data-dtm-attributes={JSON.stringify({
                  productId: this.props.context.productId,
                  exhibitorId: this.props.context.exhibitingOrganisationId,
                  package: Utils.getPackageName(this.state.packageId)
                })}
              >{this.state.companyName}
              </a>
            </div>
          </div>

          { this.state.isProductIngredientType ?
            <div className={Classes.productText}>
              <hr className={Classes.line}/>
                <Banner leftText={translations.productDetails.discover.productFieldsText}
                  centerText={translations.productDetails.discover.viewMoreText}
                  link = {`${discoverPageUrl}product/${this.props.context.productId}?external=true`}
                  dataDtm = {tags.discoverBanner}
                  dataDtmAttributes = {{productId: this.props.context.productId,
                        exhibitorId: this.props.context.exhibitingOrganisationId}}
                />
            </div>
             : Utils.isDiscoverEnabled(this.state.eventEditionId) ?
             <div className={Classes.productText}>
                <hr className={Classes.line}/>
                <Banner
                  centerText = {translations.productDetails.discover.getIngredientText}
                  link={`${discoverPageUrl}`}
                  dataDtm={tags.genericDiscoverBanner}
                />
             </div>
             : null
          }
          <ProductCarouselWrapper
            products={otherProducts}
            context={this.props.context}
            companyName={this.state.companyName}
            fireMixpanelEvent={this.handleTrackMixpanelEvent}
            tags={tags.productCarousel}
            productDetailsUrlFormat={this.props.navigation.productDetailsUrlFormat}
            interfaceLocale={this.props.context.interfaceLocale}
            productCarouselLabel={translations.productDetails.othersByExhibitor}
            artistDetailsUrlFormat={this.props.navigation.artistDetailsUrlFormat}
            productDirectoryUrlFormat={this.props.navigation.productsDirectoryUrlFormat}
            isProductDirectoryNavigationEnabled={this.state.isProductDirectoryNavigationEnabled}
            packageName={Utils.getPackageName(this.state.packageId)}
          />
        </div>
    );
  }
}

ProductDetails.propTypes = {
  idpUrl: PropTypes.string,
  baseApiUrl: PropTypes.string.isRequired,
  context: PropTypes.shape({
    productId: PropTypes.string.isRequired,
    exhibitingOrganisationId: PropTypes.string.isRequired,
    interfaceLocale: PropTypes.string.isRequired,
    primaryLocale: PropTypes.string.isRequired,
    showPlanningEnabled: PropTypes.bool,
    eventEditionId: PropTypes.string,
    mode: PropTypes.string.isRequired
  }).isRequired,
  navigation: PropTypes.object.isRequired
};


export default ProductDetails;
